import { AdoptersClient } from '../../interactors/clients/AdoptersClient'
import { GuestAdoptionsClient, GuestAdoptionLinkDto } from '../../interactors/clients/GuestAdoptionsClient'
import { AdoptionAttemptDto, AnimalDto } from '../../interactors/gen/backendClient'

import { canCreateGuestAdoption } from './canCreateGuestAdoption'

export interface AdoptionDeposit {
  depositAmount?: number
  depositDate?: string
  depositStatus?: boolean
}

export interface CreateAdoptionAttemptParams extends AdoptionDeposit {
  adopterId: string
  animalId: string
  /** The association chose to prepare the certificate and the contract through Petso. */
  shouldPrepareDocuments: boolean
  sendEmailToAdopter: boolean
  animal?: Pick<AnimalDto, 'adoption'> | null
  /** Stripe Connect is onboarded and able to take payments. */
  isStripeConnectReady: boolean
  /** A deposit was entered on the adoption form, so the fields above are worth recording. */
  hasDeposit?: boolean
  /**
   * Left out, an adoption without documents is settled from a payment link. Screens that record a
   * procedure which already happened elsewhere pass false: the adopter has nothing left to pay here.
   */
  allowGuestCheckout?: boolean
}

export interface CreateAdoptionAttemptResult {
  attemptId: string
  attempt: AdoptionAttemptDto
  /** Only set when the adoption was created as a guest one: the link to hand to the adopter. */
  guestLink?: GuestAdoptionLinkDto
}

/**
 * Single place where an adoption attempt is created, so every screen the association can start from
 * takes the same decision: with documents it is the usual procedure, without documents and with an
 * amount left to collect online it is a payment link, and otherwise it stays as it always was.
 *
 * The deposit is recorded here too, rather than by each caller, so it reaches the attempt whichever
 * branch was taken — the backend deducts a collected deposit from what the adopter is asked to pay.
 */
export const createAdoptionAttempt = async (
  params: CreateAdoptionAttemptParams
): Promise<CreateAdoptionAttemptResult> => {
  const {
    adopterId,
    animalId,
    shouldPrepareDocuments,
    sendEmailToAdopter,
    hasDeposit,
    depositAmount,
    depositDate,
    depositStatus,
  } = params

  if (params.allowGuestCheckout !== false && !shouldPrepareDocuments && canCreateGuestAdoption(params)) {
    const guestLink = await GuestAdoptionsClient.createGuestAdoption({
      animalId,
      adopterId,
      sendEmailToAdopter,
      ...(hasDeposit ? { depositAmount, depositDate, depositPaid: depositStatus } : {}),
    })

    return { attemptId: guestLink.attemptId, attempt: guestLink.attempt, guestLink }
  }

  const attempt = shouldPrepareDocuments
    ? await AdoptersClient.newAdoptionAttempt(adopterId, animalId, sendEmailToAdopter)
    : await AdoptersClient.newAdoptionAttemptAfterDocuments(adopterId, animalId, sendEmailToAdopter)

  if (!hasDeposit) {
    return { attemptId: attempt.id, attempt }
  }

  const withDeposit = await AdoptersClient.updateAdoptionAttempt(adopterId, {
    ...attempt,
    depositAmount,
    depositDate,
    depositStatus,
  })

  return { attemptId: withDeposit.id, attempt: withDeposit }
}
